import { sequenceChannel, StageRole, TrackerConfig } from './tracker-config';
import { clampFields, parseDelimitedRows } from './tracker-paste';
import {
  findDuplicatePerson,
  firstNameOf,
  fold,
  FIELD_LIMITS,
  isIsoDate,
  MAX_NEXT_STEP_LENGTH,
  parseMoneyCents,
  personName,
  stageForRole,
  TrackerBet,
  TrackerPersonFields,
} from './tracker-people';
import { deriveStage } from './tracker-rollup';
import { BOOK_A_CONVERSATION, CONVERSATION_NEXT_STEP, RECAP_SENT } from './tracker-copy';

export const MAX_IMPORT_PEOPLE = 500;

/** A touch the import writes; the server resolves `role` to the sequence channel's stage. */
export type ImportTouch =
  | {
      kind: 'step';
      role: StageRole;
      happenedOn: string;
      forOn?: string;
      label?: string;
      amountCents?: number;
    }
  | { kind: 'note'; label: string; happenedOn: string }
  | { kind: 'payment'; label: string; happenedOn: string; amountCents: number };

export type ImportPerson = TrackerPersonFields & {
  nextStep: string;
  nextStepKey?: string;
  nextOn?: string;
  touches: ImportTouch[];
  /** The stage the touches put them on, for the preview. */
  stageKey?: string;
  /** On a course with bets: the bet they go under. */
  betId?: string;
  /** Their sheet Offer matched a bet's name or offer. */
  matchedBet?: boolean;
};

export type ImportResult = {
  people: ImportPerson[];
  /** Already in the tracker (same match as Add person), so skipped. */
  duplicates: ImportPerson[];
  examples: number;
  noName: number;
  /** "Row 12, Sent Date “13/40/2026”: use a date like 2026-11-08." */
  problems: string[];
  /** Set when a file can't be read at all; nothing imports. */
  fileError?: string;
};

export const IMPORTED_SEND_LABEL = 'Imported send';
export const OFFER_IN_SHEET_LABEL = 'Offer in sheet:';
export const COLLECTED_BEFORE_IMPORT_LABEL = 'Collected before import';

const OUTREACH_HEADERS = {
  firstName: 'first name',
  fullName: 'full name',
  email: 'email',
  phone: 'phone',
  segment: 'audience / segment',
  offer: 'offer',
  tier: 'tier',
  channel: 'channel',
  nextTouch: 'next touch (beat)',
  scheduledDate: 'scheduled date',
  sentDate: 'sent date',
  status: 'status',
  nextAction: 'next action',
  nextDate: 'next date',
  touchLog: 'touch log',
  notes: 'relationship notes',
};

const PIPELINE_HEADERS = {
  firstName: 'first name',
  fullName: 'full name',
  email: 'email',
  phone: 'phone',
  offer: 'offer',
  pathStep: 'path step',
  conversationDate: 'conversation date',
  recapSent: 'recap sent',
  decisionDate: 'decision date',
  status: 'status',
  amount: 'amount (booked)',
  collected: 'collected to date',
  notes: 'notes',
};

const SHEET_DATE_LABELS: Record<string, string> = {
  scheduledDate: 'Scheduled Date',
  sentDate: 'Sent Date',
  nextDate: 'Next Date',
  conversationDate: 'Conversation Date',
  decisionDate: 'Decision Date',
};

/** Case, spacing and dash style folded, so "Replied – Interested" matches "replied - interested". */
function norm(text: string) {
  return text
    .trim()
    .toLowerCase()
    .replace(/[‐-―−]/g, '-')
    .replace(/\s+/g, ' ');
}

type Sheet<K extends string> = {
  rows: Array<{ rowNumber: number; get: (key: K) => string }>;
  error?: string;
};

function readSheet<K extends string>(
  text: string,
  headers: Record<K, string>,
  tabName: string,
  required: K[],
): Sheet<K> {
  const rows = parseDelimitedRows(text, ',');
  const headerIndex = rows.findIndex((row) =>
    required.every((key) => row.some((cell) => norm(cell) === headers[key])),
  );
  if (headerIndex < 0) {
    return {
      rows: [],
      error: `This doesn’t look like the ${tabName} tab. Download that tab as .csv and choose it again.`,
    };
  }
  const header = rows[headerIndex].map(norm);
  const index = {} as Record<K, number>;
  for (const key of Object.keys(headers) as K[]) {
    index[key] = header.indexOf(headers[key]);
  }
  return {
    rows: rows.slice(headerIndex + 1).map((row, i) => ({
      rowNumber: headerIndex + i + 2,
      get: (key: K) => (index[key] >= 0 ? (row[index[key]] ?? '').trim() : ''),
    })),
  };
}

/** YYYY-MM-DD from YYYY-MM-DD or M/D/YYYY; undefined when it is neither. */
export function parseSheetDate(text: string): string | undefined {
  const trimmed = text.trim();
  if (isIsoDate(trimmed)) {
    return trimmed;
  }
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (!match) {
    return;
  }
  const iso = `${match[3]}-${match[1].padStart(2, '0')}-${match[2].padStart(2, '0')}`;
  return isIsoDate(iso) ? iso : undefined;
}

function dateProblem(rowNumber: number, column: string, value: string) {
  return `Row ${rowNumber}, ${column} “${value}”: use a date like 2026-11-08.`;
}

function futureProblem(rowNumber: number, column: string, value: string) {
  return `Row ${rowNumber}, ${column} “${value}” is in the future. Use the day it happened.`;
}

/** "From your sheet: Touch log Intro 9/8 · Next action Details touch on the 15th" */
function sheetNote(parts: Array<[string, string]>) {
  const filled = parts.filter(([, value]) => value.trim());
  if (!filled.length) {
    return '';
  }
  return `From your sheet: ${filled.map(([label, value]) => `${label} ${value.trim()}`).join(' · ')}`;
}

function joinNote(...parts: string[]) {
  return parts
    .map((p) => p.trim())
    .filter(Boolean)
    .join('\n');
}

function isExampleRow(fullName: string) {
  return fullName.toUpperCase().includes('(EXAMPLE');
}

function personFields(
  get: (key: 'firstName' | 'fullName' | 'email' | 'phone' | 'offer') => string,
) {
  const fullName = get('fullName');
  const firstName = get('firstName') || firstNameOf(fullName);
  return { firstName, fullName, email: get('email'), phone: get('phone'), offer: get('offer') };
}

type DateReader = (column: string, value: string, opts?: { past?: boolean }) => string | undefined;

function dateReader(rowNumber: number, today: string, problems: string[]): DateReader {
  return (column, value, opts) => {
    if (!value.trim()) {
      return;
    }
    const iso = parseSheetDate(value);
    if (!iso) {
      problems.push(dateProblem(rowNumber, SHEET_DATE_LABELS[column] ?? column, value));
      return;
    }
    if (opts?.past && iso > today) {
      problems.push(futureProblem(rowNumber, SHEET_DATE_LABELS[column] ?? column, value));
      return;
    }
    return iso;
  };
}

function matchSequenceStep(config: TrackerConfig, name: string) {
  const folded = norm(name);
  return folded ? config.sequence.find((s) => norm(s.name) === folded) : undefined;
}

function earliest(a: string, b: string) {
  return a < b ? a : b;
}

/** Where in the sheet a person came from. */
type SheetRow = { tab: 'Outreach' | 'Pipeline'; row: number };

/** "Rows 2 and 3", or "Outreach row 4 and Pipeline row 4" across tabs. */
function twoRows(a?: SheetRow, b?: SheetRow) {
  if (!a || !b) {
    return 'Two rows';
  }
  if (a.tab === b.tab) {
    return `Rows ${a.row} and ${b.row}`;
  }
  return `${a.tab} row ${a.row} and ${b.tab} row ${b.row}`;
}

/**
 * Two rows of this sheet for one person that would make the same row in the
 * tracker: under the same bet, or with bets off the same offer. The second
 * is left out and named, not skipped as if it were already in the tracker.
 */
function inSheetTwiceProblem(
  rows: [SheetRow | undefined, SheetRow | undefined],
  name: string,
  offers: [string, string],
  betName?: string,
) {
  const where = twoRows(...rows);
  if (betName !== undefined) {
    const shown = offers.map((o) => o || 'no offer');
    return `${where}: ${name} is in the sheet twice (${shown.join(', ')}) and both go under “${betName}”. Import one, or put them under different bets.`;
  }
  const offer = offers[1] ? `for “${offers[1]}”` : 'with no offer';
  return `${where}: ${name} is in the sheet twice ${offer}. Keep one row, or give them different offers.`;
}

/**
 * Puts an imported person under a bet: the one whose name or offer their
 * sheet Offer matches (case-insensitive, trimmed), else the fallback bet,
 * with the sheet offer kept in the note so nothing is lost. The bet carries
 * the offer from here, so the person's own offer is left empty.
 */
function assignBet(person: ImportPerson, bets: TrackerBet[], fallbackBetId?: string) {
  const offer = fold(person.offer);
  const bet = offer
    ? bets.find((b) => fold(b.name) === offer || (!!fold(b.offer) && fold(b.offer) === offer))
    : undefined;
  if (bet) {
    person.betId = bet.id;
    person.matchedBet = true;
  } else {
    person.betId = fallbackBetId;
    person.matchedBet = false;
    if (person.offer.trim()) {
      person.note = joinNote(person.note, `${OFFER_IN_SHEET_LABEL} ${person.offer.trim()}`);
    }
  }
  person.offer = '';
}

/**
 * Reads the RIS sheet's Outreach tab (required) and Pipeline tab (optional)
 * into people with dated touches. Rows with a problem are left out and named;
 * the rest import. People already in the tracker are skipped, so importing
 * the same file twice adds nothing. With bets on, each person goes under a
 * bet (`assignBet`), and "already in the tracker" means under the same bet.
 */
export function parseRisImport(opts: {
  config: TrackerConfig;
  outreachCsv: string;
  pipelineCsv?: string;
  today: string;
  existing: Array<TrackerPersonFields & { betId?: string }>;
  /** The participant's bets, on a course with bets on. */
  bets?: TrackerBet[];
  /** The bet for people whose sheet Offer matches none. */
  fallbackBetId?: string;
}): ImportResult {
  const { config, today } = opts;
  const result: ImportResult = { people: [], duplicates: [], examples: 0, noName: 0, problems: [] };
  const channel = sequenceChannel(config);
  if (!channel) {
    result.fileError = 'Import needs an outreach sequence. This course doesn’t have one.';
    return result;
  }

  const outreach = readSheet<keyof typeof OUTREACH_HEADERS>(
    opts.outreachCsv,
    OUTREACH_HEADERS,
    'Outreach',
    ['fullName', 'status'],
  );
  if (outreach.error) {
    result.fileError = outreach.error;
    return result;
  }
  const pipeline = opts.pipelineCsv?.trim()
    ? readSheet<keyof typeof PIPELINE_HEADERS>(opts.pipelineCsv, PIPELINE_HEADERS, 'Pipeline', [
        'fullName',
        'status',
      ])
    : undefined;
  if (pipeline?.error) {
    result.fileError = pipeline.error;
    return result;
  }

  const people: ImportPerson[] = [];
  /** The sheet row each person came from, for problems that name two rows. */
  const rowOf = new Map<ImportPerson, SheetRow>();

  for (const row of outreach.rows) {
    const get = row.get;
    const fields = personFields(get);
    if (isExampleRow(fields.fullName)) {
      result.examples++;
      continue;
    }
    if (!fields.firstName && !fields.fullName) {
      result.noName++;
      continue;
    }
    const problems: string[] = [];
    const readDate = dateReader(row.rowNumber, today, problems);
    const status = norm(get('status'));
    const sentOn = readDate('sentDate', get('sentDate'), { past: true });
    const scheduledOn = readDate('scheduledDate', get('scheduledDate'));
    const nextDateOn = readDate('nextDate', get('nextDate'));
    const beat = get('nextTouch');
    const step = matchSequenceStep(config, beat);
    const person: ImportPerson = {
      ...fields,
      segment: get('segment'),
      tier: get('tier'),
      note: joinNote(
        get('notes'),
        sheetNote([
          ['Touch log', get('touchLog')],
          ['Next action', get('nextAction')],
          ['Channel', get('channel')],
        ]),
      ),
      nextStep: '',
      touches: [],
    };
    const setNext = (on?: string) => {
      if (!beat) {
        return;
      }
      person.nextStep = (step?.name ?? beat).slice(0, MAX_NEXT_STEP_LENGTH);
      person.nextStepKey = step?.key;
      person.nextOn = on;
    };
    const send = () => {
      if (!sentOn) {
        if (!get('sentDate').trim()) {
          problems.push(`Row ${row.rowNumber}, Sent Date is empty: add the day you sent it.`);
        }
        return;
      }
      person.touches.push({
        kind: 'step',
        role: 'contacted',
        happenedOn: sentOn,
        label: IMPORTED_SEND_LABEL,
      });
    };
    if (status === 'scheduled') {
      setNext(scheduledOn);
    } else if (status === 'sent') {
      send();
      setNext(nextDateOn ?? scheduledOn);
    } else if (status === 'replied - interested') {
      send();
      if (sentOn) {
        person.touches.push({ kind: 'step', role: 'replied', happenedOn: sentOn });
      }
      person.nextStep = (get('nextAction') || BOOK_A_CONVERSATION).slice(0, MAX_NEXT_STEP_LENGTH);
      person.nextOn = nextDateOn;
    } else if (status === 'replied - no') {
      send();
      if (sentOn) {
        person.touches.push({ kind: 'step', role: 'replied_no', happenedOn: sentOn });
      }
    } else if (status === 'sequence complete') {
      send();
    } else if (status) {
      problems.push(
        `Row ${row.rowNumber}, Status “${get('status')}”: use one of the sheet’s statuses, like Sent.`,
      );
    } else if (beat) {
      setNext(scheduledOn);
    }
    if (problems.length) {
      result.problems.push(...problems);
      continue;
    }
    people.push(person);
    rowOf.set(person, { tab: 'Outreach', row: row.rowNumber });
  }

  const pipelineNextSet = new Set<ImportPerson>();
  for (const row of pipeline?.rows ?? []) {
    const get = row.get;
    const fields = personFields(get);
    if (isExampleRow(fields.fullName)) {
      result.examples++;
      continue;
    }
    if (!fields.firstName && !fields.fullName) {
      result.noName++;
      continue;
    }
    const problems: string[] = [];
    const readDate = dateReader(row.rowNumber, today, problems);
    const status = norm(get('status'));
    const conversationOn = readDate('conversationDate', get('conversationDate'));
    const decisionOnDate = readDate('decisionDate', get('decisionDate'));
    const amountText = get('amount');
    const amountCents = amountText ? parseMoneyCents(amountText) : undefined;
    if (amountText && amountCents === undefined) {
      problems.push(
        `Row ${row.rowNumber}, Amount (Booked) “${amountText}”: use numbers only, like 4800.`,
      );
    }
    const collectedText = get('collected');
    const collectedCents = collectedText ? parseMoneyCents(collectedText) : undefined;
    if (collectedText && collectedCents === undefined) {
      problems.push(
        `Row ${row.rowNumber}, Collected to Date “${collectedText}”: use numbers only, like 4800.`,
      );
    }
    const known = [
      'in conversation',
      'awaiting decision',
      'won',
      'no',
      'in motion - chosen',
    ].includes(status);
    if (!known) {
      problems.push(
        status
          ? `Row ${row.rowNumber}, Status “${get('status')}”: use one of the sheet’s statuses, like In Conversation.`
          : `Row ${row.rowNumber}, Status is empty: use one of the sheet’s statuses, like In Conversation.`,
      );
    }
    if (status === 'won' && !amountCents) {
      problems.push(`Row ${row.rowNumber}, Amount (Booked) is empty: add the amount booked.`);
    }
    const name = fields.fullName.trim().toLowerCase();
    const offer = fields.offer.trim().toLowerCase();
    const email = fields.email.trim().toLowerCase();
    let person =
      people.find(
        (p) =>
          !!name && personName(p).toLowerCase() === name && p.offer.trim().toLowerCase() === offer,
      ) ?? (email ? people.find((p) => p.email.trim().toLowerCase() === email) : undefined);
    if (status === 'won' && person?.touches.some((t) => t.kind === 'step' && t.role === 'won')) {
      problems.push(
        `Row ${row.rowNumber}, Status “${get('status')}”: ${personName(
          person,
        )} is already Won in an earlier row. Keep one Won row per person.`,
      );
    }
    if (problems.length) {
      result.problems.push(...problems);
      continue;
    }
    if (!person) {
      person = { ...fields, segment: '', tier: '', note: '', nextStep: '', touches: [] };
      people.push(person);
      rowOf.set(person, { tab: 'Pipeline', row: row.rowNumber });
    }
    for (const key of ['email', 'phone', 'offer', 'firstName', 'fullName'] as const) {
      if (!person[key] && fields[key]) {
        person[key] = fields[key];
      }
    }

    const upcoming = status === 'in conversation' && !!conversationOn && conversationOn > today;
    if (!pipelineNextSet.has(person)) {
      pipelineNextSet.add(person);
      person.nextStep = '';
      person.nextStepKey = undefined;
      person.nextOn = undefined;
    }
    if (upcoming && (!person.nextOn || conversationOn < person.nextOn)) {
      person.nextStep = CONVERSATION_NEXT_STEP;
      person.nextOn = conversationOn;
    }

    const bookedOn = conversationOn ? earliest(conversationOn, today) : today;
    const touches = person.touches;
    touches.push({ kind: 'step', role: 'booked', happenedOn: bookedOn, forOn: conversationOn });
    if (conversationOn && conversationOn <= today) {
      touches.push({ kind: 'step', role: 'held', happenedOn: conversationOn });
    }
    const afterConversation = bookedOn;
    if (status !== 'in conversation' && status !== 'in motion - chosen') {
      if (norm(get('recapSent')) === 'yes') {
        touches.push({ kind: 'note', label: RECAP_SENT, happenedOn: afterConversation });
      }
      if (status === 'awaiting decision') {
        touches.push({
          kind: 'step',
          role: 'decision',
          happenedOn: afterConversation,
          forOn: decisionOnDate,
        });
      }
      const decidedOn = earliest(decisionOnDate ?? conversationOn ?? today, today);
      if (status === 'won') {
        touches.push({ kind: 'step', role: 'won', happenedOn: decidedOn, amountCents });
      }
      if (status === 'no') {
        touches.push({ kind: 'step', role: 'lost', happenedOn: decidedOn });
      }
    }
    if (collectedCents) {
      touches.push({
        kind: 'payment',
        label: COLLECTED_BEFORE_IMPORT_LABEL,
        happenedOn: today,
        amountCents: collectedCents,
      });
    }
    person.note = joinNote(
      person.note,
      status === 'in motion - chosen' ? 'In motion (chosen)' : '',
      sheetNote([
        ['Path step', get('pathStep')],
        ['Notes', get('notes')],
      ]),
    );
  }

  const seen: Array<TrackerPersonFields & { betId?: string }> = [...opts.existing];
  const imported = new Set<TrackerPersonFields>();
  const sheetOffer = new Map<TrackerPersonFields, string>();
  for (const person of people) {
    sheetOffer.set(person, person.offer.trim());
    if (config.bets) {
      assignBet(person, opts.bets ?? [], opts.fallbackBetId);
    }
    const clamped = clampFields(person);
    Object.assign(person, clamped);
    if (!person.firstName) {
      person.firstName = firstNameOf(person.fullName).slice(0, FIELD_LIMITS.firstName);
    }
    const stageTouches = person.touches.flatMap((t, i) => {
      const stage = t.kind === 'step' ? stageForRole(channel, t.role) : undefined;
      return stage
        ? [
            {
              personId: '',
              kind: 'step' as const,
              stageKey: stage.key,
              happenedOn: t.happenedOn,
              createdAt: new Date(i),
            },
          ]
        : [];
    });
    person.stageKey = deriveStage(config, channel.key, stageTouches);
    const duplicate = findDuplicatePerson(person, seen);
    if (duplicate && imported.has(duplicate)) {
      result.problems.push(
        inSheetTwiceProblem(
          [rowOf.get(duplicate as ImportPerson), rowOf.get(person)],
          personName(person),
          [sheetOffer.get(duplicate) ?? '', sheetOffer.get(person) ?? ''],
          config.bets ? (opts.bets?.find((b) => b.id === person.betId)?.name ?? '') : undefined,
        ),
      );
      continue;
    }
    if (duplicate) {
      result.duplicates.push(person);
      continue;
    }
    seen.push(person);
    imported.add(person);
    result.people.push(person);
  }
  return result;
}
